import React, { useCallback, useEffect, useState } from 'react';
import CreateGroupForm from './CreateGroupForm';
import GroupList2 from './GroupList2';
import GroupInvitations from './GroupInvitations';
import AllGroups from './AllGroups';
import { getApiBaseUrl } from '../../config/api';
import { isAdmin as isSiteAdmin } from '../../utils/userRole';
import './groupdesign/groupswrapper.css';

function resolveStoredUser(propUser) {
  if (propUser?.id) return propUser;
  try {
    const raw =
      localStorage.getItem('forumUser') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function Groupswrapper({ user: propUser, defaultTab }) {
  const API_URL = getApiBaseUrl();
  const [user, setUser] = useState(() => resolveStoredUser(propUser));
  const [myGroups, setMyGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = isSiteAdmin(user);
  const [selectedTab, setSelectedTab] = useState(() => {
    if (defaultTab === 'admin' && isSiteAdmin(resolveStoredUser(propUser))) {
      return 'admin';
    }
    return defaultTab && defaultTab !== 'admin' ? defaultTab : 'my';
  });

  useEffect(() => {
    setUser(resolveStoredUser(propUser));
  }, [propUser]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ where: 'public' });
      if (user?.id) params.set('user_id', String(user.id));

      const res = await fetch(`${API_URL}/get_groups.php?${params}`);
      const data = await res.json();

      if (data.success) {
        setMyGroups(data.my_groups || []);
        setPublicGroups(data.public_groups || []);
        setPendingInvites(data.pending_invites || []);
        setSentInvites(data.sent_invites || []);
      } else {
        console.error('Failed to fetch groups:', data.message);
      }
    } catch (err) {
      console.error('Network error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, user?.id]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const respondToInvite = async (groupId, action) => {
    if (!user?.id) return;

    try {
      const res = await fetch(`${API_URL}/respond_to_invite.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group_id: groupId,
          user_id: user.id,
          action,
        }),
      });

      const data = await res.json();
      if (data.success) {
        fetchGroups();
      } else {
        alert(data.message || 'Could not update invite.');
      }
    } catch (err) {
      console.error('Network error:', err);
    }
  };

  const deleteGroup = async (groupId) => {
    if (!user?.id) {
      alert('You must be logged in to delete a group.');
      return;
    }
    const confirmMessage = isAdmin
      ? 'Permanently delete this group now? This cannot be undone, even if someone else owns it.'
      : 'This group will be deactivated now and permanently deleted after 3 days. You can cancel this from the group page until then. Continue?';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/delete_group.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group_id: groupId,
          user_id: user.id,
          action: isAdmin ? 'purge' : 'delete',
          confirmed: true,
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert(data.message || (isAdmin ? 'Group permanently deleted.' : 'Group scheduled for deletion.'));
        fetchGroups();
      } else {
        alert(data.message || 'Failed to delete group.');
      }
    } catch (err) {
      console.error('Network error deleting group:', err);
      alert('Failed to delete group. Please try again.');
    }
  };

  const tabs = [
    user && { id: 'my', label: 'My Groups', count: myGroups.length },
    { id: 'public', label: 'Public Groups', count: publicGroups.length },
    user && {
      id: 'pending',
      label: 'Invites',
      count: pendingInvites.length,
    },
    user && { id: 'sent', label: 'Sent', count: sentInvites.length },
    isAdmin && { id: 'admin', label: 'All Groups' },
  ].filter(Boolean);

  return (
    <div className="groups-page">
      <header className="groups-page-header">
        <div>
          <h1>Groups</h1>
          <p>
            {isAdmin
              ? 'Review every group on the site. Open a group to manage it, or delete any group from this page.'
              : 'Create communities, join public groups, and open private groups you belong to.'}
          </p>
        </div>
      </header>

      {user ? (
        <CreateGroupForm user={user} onGroupCreated={fetchGroups} />
      ) : (
        <p className="groups-login-hint">Log in to create or join private groups.</p>
      )}

      <nav className="groups-tab-bar" aria-label="Group sections">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`groups-tab-btn ${selectedTab === tab.id ? 'active' : ''}`}
            onClick={() => setSelectedTab(tab.id)}
          >
            {tab.label}
            {typeof tab.count === 'number' && (
              <span className="groups-tab-count">{tab.count}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="groups-page-body">
        {loading ? (
          <p className="groups-empty">Loading groups…</p>
        ) : (
          <>
            <GroupList2
              myGroups={myGroups}
              publicGroups={publicGroups}
              selectedTab={selectedTab}
              isAdmin={isAdmin}
              user={user}
              deleteGroup={deleteGroup}
            />

            {user && (
              <GroupInvitations
                pendingInvites={pendingInvites}
                sentInvites={sentInvites}
                selectedTab={selectedTab}
                respondToInvite={respondToInvite}
              />
            )}

            {isAdmin && selectedTab === 'admin' && (
              <div className="admin-section">
                <AllGroups
                  myGroups={myGroups}
                  publicGroups={publicGroups}
                  selectedTab={selectedTab}
                  isAdmin={isAdmin}
                  user={user}
                  deleteGroup={deleteGroup}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
